import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BkgLogo } from "@/components/store/Logo";
import { StatsCards } from "@/components/admin/StatsCards";
import { ProductManager } from "@/components/admin/ProductManager";
import { OrderManager } from "@/components/admin/OrderManager";
import { PaymentManager } from "@/components/admin/PaymentManager";
import { toast } from "@/components/ui/sonner";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [productsRes, ordersRes, statsRes] = await Promise.all([
        api.get("/products"),
        api.get("/admin/orders"),
        api.get("/admin/stats"),
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (user === null) return <div className="grid min-h-screen place-items-center bg-void text-zinc-500" data-testid="admin-loading">Opening studio...</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <div className="grid min-h-screen place-items-center bg-void text-neon" data-testid="admin-access-denied">Admin access required</div>;

  const signOut = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <main className="min-h-screen bg-void px-5 py-8 text-foreground lg:px-10" data-testid="admin-dashboard">
      <header className="mb-10 flex flex-col justify-between gap-6 border-b border-white/10 pb-8 lg:flex-row lg:items-center">
        <div>
          <BkgLogo testId="admin-brand-link" />
          <h1 className="mt-4 font-heading text-5xl font-black italic tracking-tighter md:text-7xl">Admin studio</h1>
          <p className="mt-3 text-zinc-500">Signed in as {user.email}</p>
        </div>
        <div className="flex gap-3">
          <Link data-testid="view-store-link" to="/" className="border border-white/15 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:text-volt">View store</Link>
          <button data-testid="admin-logout-button" onClick={signOut} className="flex items-center gap-2 border border-neon/50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-neon"><LogOut size={16} /> Logout</button>
        </div>
      </header>
      <StatsCards stats={stats} />
      <div className="my-10 flex gap-3" data-testid="admin-tabs">
        {["products", "orders", "payments"].map((item) => (
          <button key={item} data-testid={`admin-tab-${item}`} onClick={() => setTab(item)} className={`kinetic-button border px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] ${tab === item ? "border-volt bg-volt text-black" : "border-white/15 text-zinc-400 hover:text-black"}`}>{item}</button>
        ))}
      </div>
      {loading ? <div data-testid="admin-data-loading" className="py-20 text-center text-zinc-500">Loading studio data...</div> : tab === "products" ? <ProductManager products={products} onRefresh={load} /> : tab === "orders" ? <OrderManager orders={orders} onChanged={load} /> : <PaymentManager />}
    </main>
  );
}
